import { Node, mergeAttributes } from '@tiptap/core';

/** Extract YouTube video ID from various URL formats */
function matchYouTube(url) {
  const m = url.match(
    /(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );
  return m ? m[1] : null;
}

/** Extract Instagram reel / post shortcode */
function matchInstagram(url) {
  const m = url.match(/instagram\.com\/(?:reel|p)\/([a-zA-Z0-9_-]+)/);
  return m ? m[1] : null;
}

/**
 * Auto-detect video type + ID from a pasted URL.
 * Returns { videoType: 'youtube'|'instagram', videoId } or null.
 */
export function detectVideoFromUrl(url = '') {
  const trimmed = url.trim();
  if (!trimmed) return null;

  const ytId = matchYouTube(trimmed);
  if (ytId) return { videoType: 'youtube', videoId: ytId };

  const igId = matchInstagram(trimmed);
  if (igId) return { videoType: 'instagram', videoId: igId };

  return null;
}

/** Build the iframe src for a given video type + ID */
export function getEmbedSrc(videoType, videoId) {
  if (videoType === 'youtube') return `https://www.youtube.com/embed/${videoId}`;
  if (videoType === 'instagram') return `https://www.instagram.com/reel/${videoId}/embed/`;
  return '';
}

/**
 * Tiptap custom block node that renders an embedded video iframe in the editor.
 */
export const VideoEmbed = Node.create({
  name: 'videoEmbed',
  group: 'block',
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      videoType: { default: null },
      videoId:   { default: null },
      url:       { default: null },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-video-embed]' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'div',
      mergeAttributes({ 'data-video-embed': node.attrs.videoType || '' }, HTMLAttributes),
    ];
  },

  addNodeView() {
    return ({ node }) => {
      const { videoType, videoId } = node.attrs;
      const src = getEmbedSrc(videoType, videoId);

      if (videoType === 'instagram') {
        // Portrait layout — gradient background + centred 9:16 iframe
        const outer = document.createElement('div');
        outer.style.cssText =
          'display:flex;flex-direction:column;align-items:center;justify-content:center;' +
          'background:linear-gradient(160deg,#833ab4 0%,#fd1d1d 50%,#fcb045 100%);' +
          'padding:24px 20px;border-radius:12px;margin:14px 0;gap:12px;';

        const inner = document.createElement('div');
        inner.style.cssText =
          'width:240px;aspect-ratio:9/16;border-radius:10px;overflow:hidden;' +
          'background:#000;box-shadow:0 8px 32px rgba(0,0,0,0.45);flex-shrink:0;';

        if (src) {
          const iframe = document.createElement('iframe');
          iframe.src = src;
          iframe.style.cssText = 'width:100%;height:100%;border:none;';
          iframe.setAttribute('allowfullscreen', '');
          iframe.setAttribute(
            'allow',
            'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
          );
          inner.appendChild(iframe);
        }

        outer.appendChild(inner);

        const badge = document.createElement('span');
        badge.style.cssText =
          'color:#fff;font-size:11px;font-weight:600;letter-spacing:0.04em;' +
          'background:rgba(0,0,0,0.35);padding:3px 10px;border-radius:20px;';
        badge.textContent = '📷 Instagram Reel';
        outer.appendChild(badge);

        return { dom: outer };
      }

      // ── YouTube — landscape 16:9 ──────────────────────────────────────────
      const outer = document.createElement('div');
      outer.style.cssText =
        'position:relative;padding-bottom:56.25%;height:0;overflow:hidden;' +
        'border-radius:10px;margin:14px 0;background:#0f172a;';

      if (src) {
        const iframe = document.createElement('iframe');
        iframe.src = src;
        iframe.style.cssText =
          'position:absolute;top:0;left:0;width:100%;height:100%;border:none;border-radius:10px;';
        iframe.setAttribute('allowfullscreen', '');
        iframe.setAttribute(
          'allow',
          'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
        );
        outer.appendChild(iframe);
      }

      const badge = document.createElement('span');
      badge.style.cssText =
        'position:absolute;top:8px;left:8px;background:rgba(0,0,0,0.55);' +
        'color:#fff;font-size:11px;padding:2px 8px;border-radius:4px;pointer-events:none;z-index:1;';
      badge.textContent = '▶ YouTube';
      outer.appendChild(badge);

      return { dom: outer };
    };
  },
});
